import { useState, useEffect } from 'react';
import { Usd, PriceCaption } from '@/utils/CurrencyContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import api from '@/utils/api';
import { toast } from 'sonner';
import { ShoppingCart, ArrowLeft, Package, Download, Plus, Minus, Check, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { isAuthenticated } from '@/utils/auth';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/marketplace/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/marketplace');
    } finally {
      setIsLoading(false);
    }
  };

  const addToCart = async () => {
    if (!isAuthenticated()) {
      toast.error('Please log in to add items to cart');
      return;
    }
    
    setIsAddingToCart(true);
    try {
      await api.post('/marketplace/cart/add', {
        product_id: product.id,
        quantity: quantity
      });
      toast.success(`Added ${quantity} item(s) to cart!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const buyNow = async () => {
    if (!isAuthenticated()) {
      toast.error('Please log in to purchase');
      return;
    }
    
    await addToCart();
    navigate('/marketplace/cart');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-gray-200 rounded mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-200 rounded-lg"></div>
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const isOutOfStock = product.stock === 0 && !product.is_digital;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/marketplace')}
          className="mb-6 -ml-2"
          style={{ color: '#5C635A' }}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Insight Warehouse
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="overflow-hidden" style={{ borderColor: '#E3E1DB' }}>
              <div className="relative h-80 sm:h-96 bg-gray-100">
                {product.images && product.images.length > 0 ? (
                  <img
                    src={product.images[selectedImage]}
                    alt={product.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#F5F5F0' }}>
                    <Package className="h-24 w-24" style={{ color: '#E3E1DB' }} />
                  </div>
                )}
                {product.is_digital && (
                  <Badge className="absolute top-4 right-4" style={{ backgroundColor: '#163329' }}>
                    <Download className="h-3 w-3 mr-1" />
                    Digital Download
                  </Badge>
                )}
                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">Out of Stock</span>
                  </div>
                )}
              </div>
              
              {/* Thumbnail Gallery */}
              {product.images && product.images.length > 1 && (
                <div className="flex gap-2 p-4 overflow-x-auto">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${
                        selectedImage === idx ? 'border-[#163329]' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`${product.title} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Product Info */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div className="space-y-6">
              {/* Category Badge */}
              <Badge variant="outline" className="capitalize" style={{ borderColor: '#E3E1DB', color: '#5C635A' }}>
                {product.category}
              </Badge>

              {/* Title */}
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
                {product.title}
              </h1>

              {/* Seller */}
              <div className="flex items-center gap-2" style={{ color: '#5C635A' }}>
                <User className="h-4 w-4" />
                <span>Sold by {product.seller_name}</span>
              </div>

              {/* Price */}
              <div className="text-4xl font-bold" style={{ color: '#163329' }}>
                <Usd value={product.price} />
                <PriceCaption className="font-normal text-sm" />
              </div>

              {/* Stock Status */}
              {!product.is_digital && (
                <div className="flex items-center gap-2">
                  {product.stock > 0 ? (
                    <>
                      <Check className="h-5 w-5 text-green-500" />
                      <span className="text-green-600 font-medium">In Stock ({product.stock} available)</span>
                    </>
                  ) : (
                    <span className="text-red-500 font-medium">Out of Stock</span>
                  )}
                </div>
              )}

              {/* Description */}
              <div className="prose max-w-none">
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#163329' }}>Description</h3>
                <p style={{ color: '#5C635A' }}>{product.description}</p>
              </div>

              {/* Quantity Selector */}
              {!isOutOfStock && (
                <div className="flex items-center gap-4">
                  <span className="font-medium" style={{ color: '#163329' }}>Quantity:</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className="rounded-full"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      min="1"
                      max={product.is_digital ? 1 : product.stock}
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, Math.min(product.is_digital ? 1 : product.stock, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center"
                      style={{ borderColor: '#E3E1DB' }}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.min(product.is_digital ? 1 : product.stock, quantity + 1))}
                      disabled={quantity >= (product.is_digital ? 1 : product.stock)}
                      className="rounded-full"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  className="flex-1 rounded-full"
                  style={{ backgroundColor: '#163329' }}
                  onClick={addToCart}
                  disabled={isOutOfStock || isAddingToCart}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {isAddingToCart ? 'Adding...' : 'Add to Cart'}
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  variant="outline"
                  style={{ borderColor: '#163329', color: '#163329' }}
                  onClick={buyNow}
                  disabled={isOutOfStock || isAddingToCart}
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </Button>
              </div>

              {/* Digital Product Notice */}
              {product.is_digital && (
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">Digital Download</h4>
                      <p className="text-sm text-blue-700">
                        This is a digital product. After purchase, you'll receive instant access to download.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
